import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ForceGraph3D from "react-force-graph-3d";
import * as THREE from "three";
import api from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import PageHeader from "@/components/PageHeader";
import TButton from "@/components/TButton";
import {
  Brain, Layers, Loader2, Sparkles,
  Server, Bug, Siren, X,
} from "lucide-react";
import { toast } from "sonner";

const KIND_COLOR = {
  asset:         { hex: "#06b6d4", glow: 0x06b6d4 },
  vulnerability: { hex: "#fb7185", glow: 0xfb7185 },
  incident:      { hex: "#fbbf24", glow: 0xfbbf24 },
};
const KIND_ICON = { asset: Server, vulnerability: Bug, incident: Siren };

const SEVERITY_COLOR = {
  critical: "#fb7185",
  high:     "#fb923c",
  medium:   "#fbbf24",
  low:      "#22d3ee",
  info:     "#64748b",
};

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
    <span className="text-slate-200">{children}</span>
  </div>
);

const AttackSurface3D = () => {
  const { active, activeId } = useEngagement();
  const fgRef = useRef(null);
  const [data, setData] = useState({ nodes: [], links: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiText, setAiText] = useState("");
  const [filter, setFilter] = useState({ asset: true, vulnerability: true, incident: true });

  const load = useCallback(async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const { data: g } = await api.get("/cyber-twin/graph");
      setData(g);
    } catch (e) {
      console.error("[attack-surface] load:", e);
      toast.error("Failed to load attack surface");
    } finally {
      setLoading(false);
    }
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const visibleIds = new Set(data.nodes.filter((n) => filter[n.kind]).map((n) => n.id));
    return {
      nodes: data.nodes.filter((n) => visibleIds.has(n.id)),
      links: data.links.filter((l) => {
        const s = typeof l.source === "object" ? l.source.id : l.source;
        const t = typeof l.target === "object" ? l.target.id : l.target;
        return visibleIds.has(s) && visibleIds.has(t);
      }),
    };
  }, [data, filter]);

  const handleNodeClick = (node) => {
    setSelected(node);
    setAiText("");
    if (fgRef.current && node) {
      const distance = 80;
      const distRatio = 1 + distance / Math.hypot(node.x || 1, node.y || 1, node.z || 1);
      fgRef.current.cameraPosition(
        { x: (node.x || 0) * distRatio, y: (node.y || 0) * distRatio, z: (node.z || 0) * distRatio },
        node, 1500,
      );
    }
  };

  const runAiAnalysis = async () => {
    if (!selected) return;
    setAiBusy(true);
    setAiText("");
    try {
      const { data: r } = await api.post("/cyber-twin/analyze", {
        node_id: selected.id, kind: selected.kind, label: selected.label,
        severity: selected.severity, cvss: selected.cvss, cve: selected.cve,
        ip: selected.ip, os: selected.os,
        criticality: selected.criticality, exposure: selected.exposure,
        risk_score: selected.risk_score,
      });
      setAiText(r.analysis);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI analysis failed");
    } finally {
      setAiBusy(false);
    }
  };

  const nodeThreeObject = (node) => {
    const c = KIND_COLOR[node.kind] || KIND_COLOR.asset;
    const group = new THREE.Group();
    const radius = Math.max(3, Math.min(node.val || 4, 9));
    const sphere = new THREE.Mesh(
      new THREE.SphereGeometry(radius, 16, 16),
      new THREE.MeshBasicMaterial({ color: c.glow, transparent: true, opacity: 0.95 }),
    );
    group.add(sphere);
    const halo = new THREE.Mesh(
      new THREE.SphereGeometry(radius * 1.6, 16, 16),
      new THREE.MeshBasicMaterial({ color: c.glow, transparent: true, opacity: 0.18 }),
    );
    group.add(halo);
    return group;
  };

  const linkColor = (link) => {
    if (link.kind === "asset-vuln") return "rgba(251,113,133,0.55)";
    if (link.kind === "asset-incident") return "rgba(251,191,36,0.55)";
    return "rgba(6,182,212,0.25)";
  };

  if (!activeId) {
    return (
      <div className="p-6 max-w-7xl">
        <PageHeader tag="Cyber Twin" title="3D Attack Surface" subtitle="Live force-directed graph of your engagement perimeter." />
        <div className="csx-card p-8 text-center text-slate-400">Select an engagement to load its attack surface.</div>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="attack-surface-page">
      <div className="p-6 pb-3">
        <PageHeader
          tag="Cyber Twin / 3D Graph"
          title="Attack Surface"
          subtitle={`Live force-directed graph for ${active?.name || "—"}. Click any node to interrogate it with Claude Sonnet 4.5.`}
        />
      </div>

      <div className="px-6 mb-3 flex items-center gap-3 flex-wrap">
        {Object.entries(KIND_COLOR).map(([kind, c]) => {
          const Icon = KIND_ICON[kind];
          const count = data.nodes.filter((n) => n.kind === kind).length;
          const on = filter[kind];
          return (
            <button
              key={kind}
              data-testid={`twin-filter-${kind}`}
              onClick={() => setFilter((f) => ({ ...f, [kind]: !f[kind] }))}
              className={[
                "flex items-center gap-2 px-3 py-1.5 border text-[11px] uppercase tracking-widest transition-all",
                on ? "border-slate-600 text-slate-100" : "border-slate-800 text-slate-600 line-through",
              ].join(" ")}
              style={on ? { boxShadow: `inset 0 0 0 1px ${c.hex}40`, color: c.hex } : {}}
            >
              <Icon className="h-3 w-3" style={{ color: on ? c.hex : undefined }} />
              {kind} <span className="text-slate-500">· {count}</span>
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-3 text-[10px] text-slate-500 font-mono">
          <span data-testid="stat-assets">{data.stats.assets ?? 0} assets</span>
          <span className="text-slate-700">·</span>
          <span data-testid="stat-vulns" className="text-rose-400">{data.stats.high_critical_vulns ?? 0} critical+high vulns</span>
          <span className="text-slate-700">·</span>
          <span data-testid="stat-incidents" className="text-amber-400">{data.stats.open_incidents ?? 0} open incidents</span>
          <span className="text-slate-700">·</span>
          <span data-testid="stat-edges">{data.stats.edges ?? 0} edges</span>
        </div>
      </div>

      <div className="border-y border-slate-800 bg-slate-950 relative" style={{ height: "calc(100vh - 280px)" }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-cyan-400 z-10">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2 text-xs uppercase tracking-widest">Mapping perimeter...</span>
          </div>
        )}
        {!loading && data.nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-500 z-10 px-6 text-center">
            <div>
              <Layers className="h-8 w-8 mx-auto opacity-50 mb-2" />
              <div className="text-sm">No assets discovered yet for this engagement.</div>
              <div className="text-[11px] mt-1">Run a scan or import findings to populate the graph.</div>
            </div>
          </div>
        )}
        {!loading && data.nodes.length > 0 && (
          <ForceGraph3D
            ref={fgRef}
            graphData={visible}
            backgroundColor="#020617"
            nodeLabel={(n) => `<div style="font-family:monospace;font-size:11px;background:#0f172a;color:#e2e8f0;padding:4px 8px;border:1px solid #334155">${n.kind.toUpperCase()} · ${n.label}</div>`}
            nodeThreeObject={nodeThreeObject}
            linkColor={linkColor}
            linkOpacity={0.5}
            linkWidth={(l) => (l.kind === "asset-vuln" ? 1.6 : 0.9)}
            onNodeClick={handleNodeClick}
            warmupTicks={60}
            cooldownTicks={120}
            controlType="orbit"
          />
        )}
      </div>

      {selected && (
        <aside
          data-testid="twin-node-panel"
          className="fixed right-4 top-20 w-96 max-w-[calc(100vw-2rem)] csx-card p-4 z-30 backdrop-blur bg-slate-950/95 border-cyan-500/40 shadow-2xl max-h-[80vh] overflow-y-auto"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: KIND_COLOR[selected.kind]?.hex }}>
                {selected.kind}
              </div>
              <div className="text-sm text-slate-100 font-semibold mt-1 break-words">{selected.label}</div>
            </div>
            <button data-testid="twin-panel-close" onClick={() => { setSelected(null); setAiText(""); }} className="text-slate-500 hover:text-rose-300">
              <X className="h-4 w-4" />
            </button>
          </div>

          <dl className="text-[11px] space-y-1.5 mb-4">
            {selected.severity && (
              <Row label="severity">
                <span className="font-mono uppercase" style={{ color: SEVERITY_COLOR[selected.severity] }}>
                  {selected.severity}
                </span>
              </Row>
            )}
            {selected.cvss > 0 && <Row label="cvss"><span className="font-mono text-rose-300">{selected.cvss}</span></Row>}
            {selected.cve && <Row label="cve"><span className="font-mono text-cyan-300">{selected.cve}</span></Row>}
            {selected.ip && <Row label="ip"><span className="font-mono text-slate-300">{selected.ip}</span></Row>}
            {selected.os && <Row label="os"><span className="font-mono text-slate-300">{selected.os}</span></Row>}
            {selected.criticality && <Row label="criticality">{selected.criticality}</Row>}
            {selected.exposure && <Row label="exposure">{selected.exposure}</Row>}
            {selected.risk_score > 0 && <Row label="risk score"><span className="text-amber-300">{selected.risk_score}</span></Row>}
            {selected.asset && <Row label="on asset"><span className="font-mono text-cyan-300">{selected.asset}</span></Row>}
          </dl>

          <TButton
            data-testid="twin-analyze-btn"
            className="w-full"
            icon={aiBusy ? Loader2 : Brain}
            onClick={runAiAnalysis}
            disabled={aiBusy}
          >
            {aiBusy ? "Analyzing..." : "Interrogate with Claude"}
          </TButton>

          {aiText && (
            <div data-testid="twin-analysis-text" className="mt-3 text-[11px] text-slate-300 leading-relaxed whitespace-pre-wrap border-t border-slate-800 pt-3">
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-fuchsia-300 mb-2">
                <Sparkles className="h-3 w-3" /> Claude Sonnet 4.5 · briefing
              </div>
              {aiText}
            </div>
          )}
        </aside>
      )}
    </div>
  );
};

export default AttackSurface3D;
